import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { addToCart } from "@/lib/cart";
import { useAITransition } from "@/components/AITransition";
import { toast } from "sonner";
import {
  Sparkles, UserPlus, CalendarDays, ShoppingBag, Cake, Gift, Heart,
  Clock, ArrowRight, ChevronRight, ChevronDown, Flower2, Package, Camera, Flame,
  User, IndianRupee, PartyPopper, Zap, BadgeCheck, Home,
} from "lucide-react";
import PlannerModal from "@/components/PlannerModal";

function greeting() {
  const h = new Date().getHours();
  if (h < 5) return "Good Night";
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  if (h < 21) return "Good Evening";
  return "Good Night";
}

const OCCASION_STYLES = [
  { bg: "bg-rose-50", ring: "ring-rose-100", iconBg: "bg-rose-100", iconClr: "text-rose-500", Icon: Gift, textClr: "text-rose-500" },
  { bg: "bg-violet-50", ring: "ring-violet-100", iconBg: "bg-violet-100", iconClr: "text-violet-500", Icon: Heart, textClr: "text-violet-500" },
  { bg: "bg-amber-50", ring: "ring-amber-100", iconBg: "bg-amber-100", iconClr: "text-amber-600", Icon: Gift, textClr: "text-amber-600" },
];

const RECOS = [
  { key: "flowers", title: "Beautiful Flowers", price: "Starting ₹499", image: "https://images.pexels.com/photos/34781663/pexels-photo-34781663.jpeg?auto=compress&cs=tinysrgb&w=600", tint: "from-rose-100 to-pink-50" },
  { key: "cakes", title: "Delicious Cakes", price: "Starting ₹599", image: "https://images.pexels.com/photos/19225111/pexels-photo-19225111.jpeg?auto=compress&cs=tinysrgb&w=600", tint: "from-amber-100 to-orange-50" },
  { key: "hampers", title: "Personalized Gifts", price: "Starting ₹399", image: "https://images.unsplash.com/photo-1647168672642-695e96782922?auto=format&fit=crop&w=600&q=80", tint: "from-violet-100 to-purple-50" },
  { key: "decorations", title: "Experience Moments", price: "Explore Now", image: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=600&q=80", tint: "from-emerald-100 to-teal-50" },
];

const HERO_IMG = "https://images.unsplash.com/photo-1545696563-af8f6ec2295a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const OCCASION_CARDS = [
  { label: "Birthday", image: "https://images.unsplash.com/photo-1589218909732-f304d13fbf2c?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { label: "Anniversary", image: "https://images.unsplash.com/photo-1548094967-e25a127d1f6d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { label: "Valentine's Day", image: "https://images.unsplash.com/photo-1770697410572-847984a69a56?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { label: "Raksha Bandhan", image: "https://images.unsplash.com/photo-1692902287746-ce3543c28b1e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { label: "Housewarming", image: "https://images.unsplash.com/photo-1777763517558-a911adccd1e0?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { label: "Congratulations", image: "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
];

const HERO_OCCASIONS = ["Birthday", "Anniversary", "Other"];
const HERO_MOODS = ["Simple", "Romantic", "Family", "Luxury"];
const TRUST_BADGES = [
  { icon: BadgeCheck, label: "Personalized Suggestions" },
  { icon: Package, label: "Real Products & Experiences" },
  { icon: PartyPopper, label: "For Every Occasion" },
  { icon: Zap, label: "Fast & Easy Planning" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [plannerRel, setPlannerRel] = useState(null);
  const [plannerOccasion, setPlannerOccasion] = useState("Birthday");
  const playAI = useAITransition();

  useEffect(() => {
    api.get("/relationships/upcoming?limit=6").then((r) => setUpcoming(r.data)).finally(() => setLoading(false));
    // Bottom-nav "Experiences" deep-link scroll target.
    if (window.location.hash === "#experiences") {
      setTimeout(() => {
        document.getElementById("experiences")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 400);
    }
  }, []);

  const openPlanner = async (evt) => {
    const rel = await api.get(`/relationships/${evt.relationship_id}`);
    setPlannerRel(rel.data);
    setPlannerOccasion(evt.occasion === "anniversary" ? "Anniversary" : "Birthday");
  };

  return (
    <div data-testid="dashboard-page" className="space-y-8 sm:space-y-10">
      {/* 1 — HERO: the primary AI entry (opens the existing planner with context) */}
      <HeroSection navigate={navigate} />

      {/* 2 — Shop by Occasion */}
      <ShopByOccasionSection navigate={navigate} />

      {/* 3 — Make It More Memorable */}
      <MakeItMemorableSection navigate={navigate} />

      {/* 4 — Trending on CelebrAI */}
      <TrendingSection navigate={navigate} />

      {/* 5 — Upcoming Occasions (existing) */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl sm:text-3xl text-charcoal">Upcoming Occasions</h2>
          <button onClick={() => navigate("/calendar")} data-testid="view-calendar-link"
            className="flex items-center gap-1 text-sm font-semibold text-terracotta hover:opacity-80">
            View Calendar <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 gap-3">
            {[...Array(4)].map((_, i) => <div key={i} className="card-surface h-24 animate-pulse bg-linen-2" />)}
          </div>
        ) : upcoming.length === 0 ? (
          <div className="card-surface p-8 text-center" data-testid="empty-upcoming">
            <Cake className="w-8 h-8 text-terracotta mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-charcoal/70">No occasions yet — add someone to remember.</p>
            <button onClick={() => navigate("/relationships?new=1")} className="pill-btn pill-btn-primary mt-4" data-testid="empty-add-rel">Add Someone</button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {upcoming.slice(0, 6).map((evt, i) => (
              <UpcomingCard key={`${evt.relationship_id}-${evt.occasion}`} evt={evt} idx={i} onClick={() => openPlanner(evt)} />
            ))}
          </div>
        )}
      </section>

      {/* 6 — Quick Actions (existing) */}
      <section>
        <h2 className="font-display text-2xl sm:text-3xl text-charcoal mb-4">Quick Actions</h2>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          <QuickTile onClick={() => navigate("/relationships?new=1")} icon={UserPlus} label="Add Relationship"
            iconBg="bg-violet-100" iconClr="text-violet-600" testId="qa-add-relationship" />
          <QuickTile onClick={(e) => playAI(() => navigate("/planner"), e)} icon={Gift} label="Plan Celebration"
            iconBg="bg-rose-100" iconClr="text-rose-500" testId="qa-plan-celebration" />
          <QuickTile onClick={() => navigate("/calendar")} icon={CalendarDays} label="Calendar"
            iconBg="bg-amber-100" iconClr="text-amber-600" testId="qa-calendar" />
          <QuickTile onClick={() => navigate("/bookings")} icon={Clock} label="My Celebrations"
            iconBg="bg-emerald-100" iconClr="text-emerald-600" testId="qa-celebrations" />
          <QuickTile onClick={() => navigate("/bookings")} icon={ShoppingBag} label="My Orders"
            iconBg="bg-sky-100" iconClr="text-sky-600" testId="qa-orders" />
        </div>
      </section>

      {/* 7 — Recommended For You (existing) */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl sm:text-3xl text-charcoal">Recommended For You</h2>
          <button onClick={() => navigate("/shop")} data-testid="view-all-recos"
            className="flex items-center gap-1 text-sm font-semibold text-terracotta hover:opacity-80">
            View All <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {RECOS.map((r, i) => (
            <RecoCard key={r.key} r={r} idx={i} onClick={() => navigate(`/shop?cat=${r.key}`)} />
          ))}
        </div>
      </section>

      {/* 8 — Plan Ahead banner (existing) */}
      <section>
        <div className="card-surface p-6 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center gap-5 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100"
          data-testid="plan-ahead-banner">
          <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <CalendarDays className="w-7 h-7 text-amber-600" strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-lg sm:text-xl text-charcoal">Plan Ahead, Celebrate Better</p>
            <p className="text-sm text-charcoal/70 mt-1">AI helps you plan the perfect celebration with personalized ideas.</p>
          </div>
          <button onClick={(e) => playAI(() => navigate("/planner"), e)} data-testid="plan-ahead-cta"
            className="rounded-full px-5 py-2.5 text-sm font-bold bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/40 transition whitespace-nowrap">
            Plan Now
          </button>
        </div>
      </section>

      {plannerRel && (
        <PlannerModal
          relationship={plannerRel}
          defaultOccasion={plannerOccasion}
          onClose={() => setPlannerRel(null)}
        />
      )}
    </div>
  );
}

/* ---------- HERO ---------- */

function HeroSection({ navigate }) {
  const [rels, setRels] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [relId, setRelId] = useState("");
  const [occasion, setOccasion] = useState("");
  const [mood, setMood] = useState("");
  const [budget, setBudget] = useState("");
  const playAI = useAITransition();

  useEffect(() => {
    api.get("/relationships").then((r) => setRels(r.data || [])).catch(() => {});
    api.get("/planner/budget-tiers").then((r) => setTiers((r.data || []).filter((t) => !t.is_custom))).catch(() => {});
  }, []);

  const generate = (e) => {
    const params = new URLSearchParams();
    if (relId) params.set("rel", relId);
    if (occasion) params.set("occasion", occasion);
    if (mood) params.set("mood", mood);
    if (budget) params.set("tier", budget);
    const url = `/planner?${params.toString()}`;
    // Hand the relationship object over via router state so the planner modal
    // mounts synchronously with the navigation — no fetch gap under the
    // transition overlay (root cause of the open/close flicker).
    const rel = rels.find((r) => r.id === relId) || null;
    playAI(() => navigate(url, rel ? { state: { relationship: rel } } : undefined), e);
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      data-testid="hero-section"
      className="relative overflow-hidden rounded-3xl shadow-lg shadow-rose-500/5">
      {/* Background image + soft left fade for readability */}
      <img src={HERO_IMG} alt="" aria-hidden
        className="absolute inset-0 w-full h-full object-cover object-center" />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/20" />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-transparent" />

      <div className="relative p-5 sm:p-8 lg:p-10 max-w-2xl">
        <div className="inline-flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-rose-500" strokeWidth={2.25} />
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-charcoal">Plan with AI</span>
        </div>

        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-charcoal leading-tight font-semibold" data-testid="hero-headline">
          Turn your moments into meaningful{" "}
          <span className="gradient-text-rose-purple">celebrations</span>
        </h1>
        <p className="mt-3 text-sm sm:text-base text-charcoal/70 leading-relaxed max-w-lg">
          Tell us a few details and let CelebrAI create a personalised celebration plan with the perfect gifts, experiences and more.
        </p>

        {/* Context dropdown pills */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="hero-filters">
          <HeroSelect icon={User} label="For whom?" value={relId} onChange={setRelId} testId="hero-rel"
            options={rels.map((r) => ({ value: r.id, label: r.name }))} />
          <HeroSelect icon={Gift} label="Occasion" value={occasion} onChange={setOccasion} testId="hero-occasion"
            options={HERO_OCCASIONS.map((o) => ({ value: o, label: o }))} />
          <HeroSelect icon={Heart} label="Mood" value={mood} onChange={setMood} testId="hero-mood"
            options={HERO_MOODS.map((m) => ({ value: m, label: m }))} />
          <HeroSelect icon={IndianRupee} label="Budget" value={budget} onChange={setBudget} testId="hero-budget"
            options={tiers.map((t) => ({ value: t.key, label: t.label }))} />
        </div>

        <button onClick={generate} data-testid="hero-generate"
          className="mt-4 w-full rounded-full px-6 py-4 text-sm sm:text-base font-bold text-white shadow-xl shadow-rose-500/40 hover:-translate-y-0.5 hover:shadow-2xl transition-all flex items-center justify-center gap-2"
          style={{ background: "linear-gradient(135deg, #E11D48 0%, #A855F7 100%)" }}>
          <Sparkles className="w-5 h-5" strokeWidth={2.25} />
          Generate My Celebration Plan <ArrowRight className="w-5 h-5" strokeWidth={2.25} />
        </button>

        {/* Trust badges */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="hero-trust-badges">
          {TRUST_BADGES.map((b) => (
            <div key={b.label} className="flex items-center gap-2 min-w-0">
              <b.icon className="w-4 h-4 text-rose-500 flex-shrink-0" strokeWidth={2.25} />
              <span className="text-[11px] sm:text-xs font-semibold text-charcoal/80 leading-tight">{b.label}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.section>
  );
}

function HeroSelect({ icon: Icon, label, value, onChange, options, testId }) {
  const selected = options.find((o) => o.value === value);
  return (
    <div className="relative rounded-2xl bg-white shadow-sm border border-warm px-3 py-2.5">
      <div className="flex items-center gap-2 pointer-events-none min-w-0">
        <Icon className="w-4 h-4 text-rose-500 flex-shrink-0" strokeWidth={2} />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold text-charcoal uppercase tracking-wide leading-tight">{label}</p>
          <p className={`text-xs leading-tight truncate ${selected ? "text-charcoal font-semibold" : "text-charcoal/45"}`}>
            {selected ? selected.label : "Select"}
          </p>
        </div>
        <ChevronDown className="w-3.5 h-3.5 text-charcoal/40 flex-shrink-0" strokeWidth={2.25} />
      </div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        aria-label={label}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      >
        <option value="">Select</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

/* ---------- SHOP BY OCCASION ---------- */

function ShopByOccasionSection({ navigate }) {
  const playAI = useAITransition();
  const go = (occasion, e) =>
    playAI(() => navigate(`/planner?occasion=${encodeURIComponent(occasion)}`), e);
  return (
    <section data-testid="shop-by-occasion-section">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-display text-2xl sm:text-3xl text-charcoal">Shop by Occasion</h2>
        <button onClick={() => navigate("/shop")} data-testid="occasion-view-all"
          className="flex items-center gap-1 text-sm font-semibold text-rose-500 hover:opacity-80 whitespace-nowrap">
          View All <ArrowRight className="w-4 h-4" strokeWidth={2.25} />
        </button>
      </div>
      <p className="text-sm text-charcoal/60 mb-4">Find the perfect gifts and experiences for every special moment.</p>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x sm:grid sm:grid-cols-3 lg:grid-cols-6 sm:overflow-visible">
        {OCCASION_CARDS.map((o, i) => (
          <motion.button key={o.label}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            onClick={(e) => go(o.label, e)}
            data-testid={`occasion-card-${o.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="flex-shrink-0 w-32 sm:w-auto snap-start text-left group">
            <div className="aspect-square rounded-2xl overflow-hidden bg-linen-2 shadow-sm group-hover:shadow-md transition-shadow">
              <img src={o.image} alt={o.label} loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <p className="mt-2 text-sm font-semibold text-charcoal text-center leading-tight">{o.label}</p>
          </motion.button>
        ))}
      </div>
    </section>
  );
}

/* ---------- MAKE IT MORE MEMORABLE ---------- */

const EXP_BADGES = ["Most Loved", "Trending"];

function MakeItMemorableSection({ navigate }) {
  const [exps, setExps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState({});
  useEffect(() => {
    api.get("/experiences").then((r) => setExps((r.data || []).slice(0, 6)))
      .finally(() => setLoading(false));
  }, []);
  if (!loading && exps.length === 0) return null;
  return (
    <section data-testid="make-it-memorable-section" id="experiences">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-display text-2xl sm:text-3xl text-charcoal">👑 Make It More Memorable</h2>
        <button onClick={() => navigate("/planner")} data-testid="experiences-view-all"
          className="flex items-center gap-1 text-sm font-semibold text-rose-500 hover:opacity-80 whitespace-nowrap">
          View All <ArrowRight className="w-4 h-4" strokeWidth={2.25} />
        </button>
      </div>
      <p className="text-sm text-charcoal/60 mb-4">Unique experiences to make your celebration truly special.</p>
      {loading ? (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {[...Array(3)].map((_, i) => <div key={i} className="card-surface w-60 h-64 flex-shrink-0 animate-pulse bg-linen-2" />)}
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:overflow-visible">
          {exps.map((e, i) => (
            <motion.div key={e.id}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              data-testid={`experience-card-${e.id}`}
              onClick={() => navigate(`/experiences/${e.id}`)}
              className="flex-shrink-0 w-60 sm:w-auto snap-start card-surface overflow-hidden cursor-pointer hover:shadow-lg transition-shadow flex flex-col">
              <div className="relative aspect-[4/3] bg-linen-2 overflow-hidden">
                {e.image_url ? (
                  <img src={e.image_url} alt={e.name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Sparkles className="w-8 h-8 text-rose-300" strokeWidth={1.75} />
                  </div>
                )}
                <span className="absolute top-2.5 left-2.5 rounded-full bg-white/95 backdrop-blur px-2.5 py-1 text-[10px] font-bold text-rose-600 shadow-sm flex items-center gap-1"
                  data-testid={`experience-badge-${e.id}`}>
                  {i === 0 ? "✨" : "🔥"} {EXP_BADGES[i % EXP_BADGES.length]}
                </span>
                <button
                  onClick={(ev) => { ev.stopPropagation(); setSaved((s) => ({ ...s, [e.id]: !s[e.id] })); }}
                  aria-label={saved[e.id] ? "Unsave" : "Save"}
                  data-testid={`experience-save-${e.id}`}
                  className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-white/95 backdrop-blur shadow-sm flex items-center justify-center">
                  <Heart className={`w-4 h-4 ${saved[e.id] ? "text-rose-500 fill-rose-500" : "text-charcoal/60"}`} strokeWidth={2} />
                </button>
              </div>
              <div className="p-3.5 flex-1 flex flex-col">
                <p className="font-display text-base font-semibold text-charcoal leading-tight flex items-center gap-1.5 break-words">
                  <Sparkles className="w-3.5 h-3.5 text-rose-500 flex-shrink-0" strokeWidth={2.25} />
                  {e.name}
                </p>
                {e.description && (
                  <p className="text-xs text-charcoal/60 mt-1 line-clamp-1">{e.description}</p>
                )}
                <div className="mt-auto pt-3 flex items-center justify-between">
                  <p className="font-display text-base font-bold text-charcoal" data-testid={`experience-price-${e.id}`}>
                    {e.price_type === "on_request" ? "Price on Request" : `₹${Number(e.price || 0).toLocaleString("en-IN")}`}
                  </p>
                  <span className="w-9 h-9 rounded-full bg-charcoal text-white flex items-center justify-center shadow-md">
                    <ArrowRight className="w-4 h-4" strokeWidth={2.25} />
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ---------- TRENDING ON CELEBRAI ---------- */

function TrendingSection({ navigate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState({});
  useEffect(() => {
    api.get("/products/trending?limit=8")
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  }, []);
  const add = (p) => {
    addToCart({
      line_key: p.id, kind: "product", product_id: p.id,
      name: p.name, category: p.category, price: Number(p.price || 0), quantity: 1,
      image_url: p.image_url || "", is_global: !!p.is_global,
      delivery_pincode: p.delivery_pincode || null,
    });
    toast.success(`${p.name} added to cart`);
  };
  if (!loading && items.length === 0) return null;
  return (
    <section data-testid="trending-section">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-display text-2xl sm:text-3xl text-charcoal flex items-center gap-2">
          🔥 Trending on CelebrAI
        </h2>
        <button onClick={() => navigate("/shop")} data-testid="trending-view-all"
          className="flex items-center gap-1 text-sm font-semibold text-rose-500 hover:opacity-80 whitespace-nowrap">
          View All <ArrowRight className="w-4 h-4" strokeWidth={2.25} />
        </button>
      </div>
      <p className="text-sm text-charcoal/60 mb-4">Most loved gifts and experiences this season.</p>
      {loading ? (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {[...Array(4)].map((_, i) => <div key={i} className="card-surface w-44 h-60 flex-shrink-0 animate-pulse bg-linen-2" />)}
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x sm:grid sm:grid-cols-3 lg:grid-cols-4 sm:overflow-visible">
          {items.map((p, i) => (
            <motion.div key={p.id}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="flex-shrink-0 w-44 sm:w-auto snap-start card-surface overflow-hidden flex flex-col hover:shadow-lg transition-shadow"
              data-testid={`trending-card-${p.id}`}>
              <div className="relative aspect-square bg-linen-2 overflow-hidden">
                <button onClick={() => navigate(`/product/${p.id}`)} className="w-full h-full" aria-label={`View ${p.name}`}>
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Package className="w-8 h-8 text-charcoal/30" /></div>
                  )}
                </button>
                <button
                  onClick={() => setSaved((s) => ({ ...s, [p.id]: !s[p.id] }))}
                  aria-label={saved[p.id] ? "Unsave" : "Save"}
                  data-testid={`trending-save-${p.id}`}
                  className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-white/95 backdrop-blur shadow-sm flex items-center justify-center">
                  <Heart className={`w-4 h-4 ${saved[p.id] ? "text-rose-500 fill-rose-500" : "text-charcoal/60"}`} strokeWidth={2} />
                </button>
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <p className="text-sm font-semibold text-charcoal leading-tight break-words line-clamp-2">{p.name}</p>
                <p className="font-display text-base font-bold text-charcoal mt-1">₹{Number(p.price || 0).toLocaleString("en-IN")}</p>
                <button onClick={() => add(p)}
                  data-testid={`trending-add-${p.id}`}
                  className="mt-2 w-full rounded-full px-3 py-2 text-xs font-bold border-2 border-rose-500 text-rose-600 hover:bg-rose-500 hover:text-white transition-colors flex items-center justify-center gap-1.5">
                  <ShoppingBag className="w-3.5 h-3.5" strokeWidth={2.25} /> Add
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ---------- Existing sub-components (unchanged) ---------- */

function UpcomingCard({ evt, idx, onClick }) {
  const style = OCCASION_STYLES[idx % OCCASION_STYLES.length];
  const { Icon } = style;
  return (
    <motion.button
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}
      onClick={onClick}
      data-testid={`upcoming-card-${idx}`}
      className={`card-surface p-4 text-left flex items-center gap-3 ${style.bg} border-transparent ring-1 ${style.ring}`}>
      {evt.picture ? (
        <img src={evt.picture} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-white" />
      ) : (
        <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center font-bold text-charcoal ring-2 ring-white">
          {evt.name.slice(0, 1).toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-charcoal truncate flex items-center gap-1">
          {evt.name}'s {evt.occasion === "anniversary" ? "Anniversary" : "Birthday"}
          <span aria-hidden className="text-sm">{evt.occasion === "anniversary" ? "❤️" : "🎂"}</span>
        </p>
        <p className="text-xs text-charcoal/60 mt-0.5">{evt.date}</p>
        <p className={`text-xs font-bold ${style.textClr} mt-0.5`}>{evt.days_left} Days Left</p>
      </div>
      <div className={`w-11 h-11 rounded-2xl ${style.iconBg} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 ${style.iconClr} ${evt.occasion === "anniversary" ? "fill-current" : ""}`} strokeWidth={evt.occasion === "anniversary" ? 0 : 1.75} />
      </div>
    </motion.button>
  );
}

function QuickTile({ onClick, icon: Icon, label, iconBg, iconClr, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="card-surface p-3 sm:p-4 flex flex-col items-center gap-2 hover:shadow-lg">
      <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-2xl ${iconBg} flex items-center justify-center`}>
        <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${iconClr}`} strokeWidth={1.75} />
      </div>
      <span className="text-[11px] sm:text-xs font-semibold text-charcoal text-center leading-tight">{label}</span>
    </button>
  );
}

function RecoCard({ r, idx, onClick }) {
  return (
    <motion.button
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}
      onClick={onClick}
      data-testid={`reco-card-${r.key}`}
      className={`relative rounded-3xl overflow-hidden text-left bg-gradient-to-br ${r.tint} shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all group`}>
      <div className="aspect-square overflow-hidden">
        <img src={r.image} alt={r.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
      </div>
      <div className="p-3 sm:p-4">
        <p className="font-display text-sm sm:text-base font-semibold text-charcoal leading-tight">{r.title}</p>
        <div className="mt-2 flex items-center justify-between">
          <p className="text-xs text-charcoal/70">{r.price}</p>
          <div className="w-7 h-7 rounded-full bg-white shadow-sm flex items-center justify-center">
            <ArrowRight className="w-3.5 h-3.5 text-rose-500" strokeWidth={2.25} />
          </div>
        </div>
      </div>
    </motion.button>
  );
}
